import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { apiUrl } from "../ApiUrl/ApiUrl";

const Invoiceadmin = () => {
  const location = useLocation();
  const invoiceRef = useRef(null);
  const actionToolbarRef = useRef(null);

  const [invoiceData, setInvoiceData] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fetchedOrderId = urlParams.get("orderId");
    if (!fetchedOrderId) {
      setIsLoading(false);
      return;
    }

    setOrderId(fetchedOrderId);

    fetch(`${apiUrl}/public/invoiceadmin?orderId=${fetchedOrderId}`)
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`Admin invoice fetch failed (Status ${resp.status})`);
        }
        return resp.json();
      })
      .then((data) => {
        setInvoiceData(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching invoice:", error);
        setIsLoading(false);
      });
  }, [location.search]);

  const handlePrint = () => {
    window.print();
  };

  const generatePDF = () => {
    if (!window.html2canvas || !window.jspdf) {
      window.print();
      return;
    }

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF("p", "mm", "a4");

    if (actionToolbarRef.current) {
      actionToolbarRef.current.style.display = "none";
    }

    window.html2canvas(invoiceRef.current, { scale: 2, useCORS: true }).then((canvas) => {
      if (actionToolbarRef.current) {
        actionToolbarRef.current.style.display = "flex";
      }

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      doc.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      doc.save(`Admin_Invoice_${orderId || "NavneetLaw"}.pdf`);
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-8">
        <div className="w-12 h-12 border-4 border-[#162237] border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-[#162237] font-semibold text-lg">Retrieving Admin Records...</p>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-gray-100 rounded-2xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
            !
          </div>
          <h3 className="text-xl font-bold text-[#162237] mb-2">Invoice Not Found</h3>
          <p className="text-gray-500 text-sm mb-6">
            Admin invoice record could not be located for order #{orderId}.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-5 py-2.5 bg-[#162237] hover:bg-[#0B132B] text-white font-medium rounded-xl text-sm transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const items = invoiceData.items || [];
  const itemsTotal = items.reduce(
    (acc, item) => acc + (item.itemPrice != null ? Number(item.itemPrice) : 0) * (item.qty != null ? Number(item.qty) : 1),
    0
  );
  const gstTotal = items.reduce((acc, item) => acc + (item.gst != null ? Number(item.gst) : 0), 0);
  const totalAmount = invoiceData.totalAmount != null ? Number(invoiceData.totalAmount) : itemsTotal + gstTotal;
  const isMaharashtra = invoiceData.state && invoiceData.state.toLowerCase() === "maharashtra";

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 px-4 sm:px-6">
      {/* Action Bar */}
      <div
        ref={actionToolbarRef}
        className="max-w-4xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-4 print:hidden bg-white p-4 rounded-2xl border border-gray-100 shadow-sm"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="text-xs font-semibold text-gray-600 hover:text-[#162237] flex items-center gap-1.5 transition"
          >
            ← Back
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full">
            Admin Copy
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handlePrint}
            className="px-4 py-2 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition flex items-center gap-1.5"
          >
            🖨️ Print
          </button>
          <button
            onClick={generatePDF}
            className="px-5 py-2 text-xs font-bold text-white bg-[#162237] hover:bg-[#0B132B] rounded-xl transition shadow-sm flex items-center gap-1.5"
          >
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* Invoice Document Box */}
      <div
        ref={invoiceRef}
        className="max-w-4xl mx-auto bg-white border border-gray-200 shadow-lg rounded-2xl p-8 sm:p-12 text-[#162237] font-sans print:border-none print:shadow-none print:p-4 print:m-0"
      >
        {/* Document Header */}
        <div className="border-b-2 border-gray-100 pb-6 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-[#162237] text-white text-[11px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded">
                  TAX INVOICE
                </span>
                <span className="text-gray-400 text-xs font-medium">Administrator Duplicate</span>
              </div>
              <h1 className="text-2xl font-black text-[#162237] tracking-tight">
                Navneet Law Studio Pvt. Ltd.
              </h1>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-sm">
                Bldg 4B-605, SDPL Ashray, Beltarodi, Nagpur, MH – 440037, India<br />
                <strong>GSTIN:</strong> 27AAJCN6443N1ZU | <strong>State Code:</strong> 27 (Maharashtra)<br />
                <strong>Direct Desk:</strong> +91 84213 85239 | <strong>Email:</strong> customercare@navneetlawstudio.com
              </p>
            </div>

            <div className="text-left sm:text-right">
              <div className="bg-[#F8FAFC] border border-gray-100 rounded-xl p-3.5 inline-block text-left sm:text-right">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Invoice / Order Ref</p>
                <p className="text-lg font-black text-[#162237] font-mono">#{invoiceData.orderId || orderId}</p>
                <p className="text-xs text-gray-500 mt-1">
                  <strong>Date:</strong> {invoiceData.dateadded || new Date().toLocaleDateString('en-IN')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Billed To Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-[#F8FAFC] border border-gray-100 rounded-xl p-5 mb-8">
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Billed & Delivered To:</p>
            <p className="text-base font-bold text-[#162237]">{invoiceData.cname || "Consignee / Legal Practitioner"}</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed whitespace-pre-line">
              {invoiceData.address || "Chambers / Registered Address"}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              {[invoiceData.city, invoiceData.state].filter(Boolean).join(", ")}
              {invoiceData.pincode ? ` - ${invoiceData.pincode}` : ""}
            </p>
          </div>

          <div className="sm:text-right">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Recipient Coordinates:</p>
            <p className="text-xs text-gray-600">
              <strong>Phone:</strong> {invoiceData.contactNumber || "N/A"}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              <strong>Email:</strong> {invoiceData.email || "N/A"}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              <strong>Place of Supply:</strong> {invoiceData.state || "Maharashtra"}
            </p>
          </div>
        </div>

        {/* Itemized Table */}
        <div className="overflow-x-auto mb-8 border border-gray-200 rounded-xl">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-[#162237] text-white">
                <th className="py-3 px-4 font-bold">#</th>
                <th className="py-3 px-4 font-bold">Item Description / Citation Title</th>
                <th className="py-3 px-4 font-bold text-center">Qty</th>
                <th className="py-3 px-4 font-bold text-right">Unit Price</th>
                <th className="py-3 px-4 font-bold text-right">GST Rate</th>
                <th className="py-3 px-4 font-bold text-right">Net Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, idx) => {
                const price = item.itemPrice != null ? Number(item.itemPrice) : 0;
                const gst = item.gst != null ? Number(item.gst) : 0;
                const qty = item.qty != null ? Number(item.qty) : 1;
                const rowTotal = price * qty + gst;
                return (
                  <tr key={item.id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                    <td className="py-3 px-4 text-gray-400 font-mono">{idx + 1}</td>
                    <td className="py-3 px-4 font-medium text-[#162237]">
                      {item.bookTitle || item.name || "Judicial Publication / Software License"}
                    </td>
                    <td className="py-3 px-4 text-center font-bold text-[#162237]">{qty}</td>
                    <td className="py-3 px-4 text-right text-gray-600">₹{price.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-gray-600">₹{gst.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-bold text-[#162237]">₹{rowTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals & Tax Calculation Breakdown */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-8 mb-8">
          <div className="flex-1 bg-[#F8FAFC] border border-gray-100 rounded-xl p-5 text-xs">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Remittance Bank</p>
            <div className="space-y-1 text-gray-700">
              <p><strong>Bank:</strong> HDFC BANK, MANISH NAGAR</p>
              <p><strong>Account Name:</strong> Navneet Law Studio Pvt Ltd</p>
              <p><strong>Account Number:</strong> 50200098479361</p>
              <p><strong>IFSC Code:</strong> HDFC0003632</p>
            </div>
          </div>

          <div className="w-full sm:w-72 space-y-2.5 text-xs text-gray-700">
            <div className="flex justify-between py-1 border-b border-gray-100">
              <span className="text-gray-500">Taxable Subtotal:</span>
              <span className="font-semibold text-[#162237]">₹{itemsTotal.toFixed(2)}</span>
            </div>

            {isMaharashtra ? (
              <>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">CGST:</span>
                  <span className="font-semibold text-[#162237]">₹{(gstTotal / 2).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">SGST:</span>
                  <span className="font-semibold text-[#162237]">₹{(gstTotal / 2).toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">IGST:</span>
                <span className="font-semibold text-[#162237]">₹{gstTotal.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between items-center py-2.5 bg-gray-50 px-3 rounded-lg border border-gray-100">
              <span className="font-bold text-sm text-[#162237]">Total Amount:</span>
              <span className="font-black text-base text-[#162237]">₹{totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Declarations */}
        <div className="border-t border-gray-200 pt-6 mt-8">
          <div className="flex flex-col sm:flex-row justify-between items-end gap-6 text-xs text-gray-500">
            <div className="max-w-md space-y-1">
              <p className="font-bold text-gray-700">Internal Audit & Administrative Record</p>
              <p>Computer generated invoice copy for administrative and reconciliation recordkeeping.</p>
            </div>
            <div className="text-center sm:text-right border-t sm:border-t-0 pt-4 sm:pt-0">
              <p className="font-bold text-[#162237]">For Navneet Law Studio Pvt. Ltd.</p>
              <p className="text-[11px] text-gray-400 mt-6">Authorized Signatory</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoiceadmin;
